<script>
  /**
   * Saved replies — the manager's own answers to recurring guest situations.
   *
   * WHAT THE MANAGER IS ACTUALLY DOING HERE
   * ---------------------------------------
   * Writing down, once, how they personally handle something the auto-responder
   * cannot answer from property facts. The prod case this came from: a guest
   * asked to check in at 2, retrieval correctly found "check-in is 4pm" and
   * answered with the policy — while the real reply was "Checking with cleaners
   * now". The 4pm fact is the premise of the question, not an answer to it.
   *
   * TWO THINGS IN HERE ARE LOAD-BEARING, NOT DECORATION
   * ---------------------------------------------------
   * 1. The "try it" box. A description the classifier cannot act on is the
   *    feature's quiet failure mode, and there is otherwise no way to find out
   *    short of watching real guest traffic. It turns "did I describe this well
   *    enough" into something you just try.
   * 2. Repeater rows carry a client-side `uid` and the {#each} is keyed on it,
   *    never on the array index. With index keys, removing a middle row leaves
   *    every `bind:value` below it pointing at the wrong element — presenting as
   *    "the wrong text moved", which is very hard to read as a keying bug.
   *    The separate server `id` is a different thing entirely and is preserved
   *    on save: it is what links a past draft to the wording it came from.
   */
  import api from '$lib/api.js';
  import { toast } from '$lib/toast.svelte.js';
  import { authStore } from '../stores/authStore.svelte.js';
  import PageHeader from '../components/PageHeader.svelte';
  import DataTable from '../components/DataTable.svelte';
  import EmptyState from '../components/EmptyState.svelte';
  import ConfirmModal from '../components/ConfirmModal.svelte';

  let loading = $state(true);
  let error = $state(null);
  let items = $state([]);

  let editTarget = $state(null);      // the row being edited, or 'new'
  let form = $state(null);
  let saving = $state(false);
  let confirmTarget = $state(null);

  let testMessage = $state('');
  let testing = $state(false);
  let testResult = $state(null);

  //: Every per-tenant endpoint needs this explicitly — the server refuses with
  //: "Select a tenant first" rather than guessing which tenant a super-user means.
  //: Same pattern as routes/TenantSettings.svelte.
  const tenantId = $derived(authStore.tenantId ?? undefined);

  /** Stable key for a repeater row. Never the index — see the header comment. */
  function uid() {
    return globalThis.crypto?.randomUUID?.() ?? `r${Math.random().toString(36).slice(2)}`;
  }

  function row(value = '', id = null) {
    return { uid: uid(), id, value };
  }

  function blankForm() {
    return {
      name: '',
      description: '',
      examples: [row()],
      replies: [row()],
      priority: 100,
      is_active: true,
    };
  }

  function formFor(situation) {
    return {
      name: situation.name,
      description: situation.description,
      examples: situation.example_messages.length
        ? situation.example_messages.map((m) => row(m))
        : [row()],
      // Server variant ids ride along so the save updates rows in place.
      replies: situation.variants.length
        ? situation.variants.map((v) => row(v.body, v.id))
        : [row()],
      priority: situation.priority,
      is_active: situation.is_active,
    };
  }

  function openNew(prefill = null) {
    editTarget = 'new';
    form = prefill ? { ...blankForm(), ...prefill } : blankForm();
    testResult = null;
  }

  function openEdit(situation) {
    editTarget = situation;
    form = formFor(situation);
    testResult = null;
  }

  function onEditorKey(e) {
    // Escape closes the editor, but not while the delete confirmation is up —
    // ConfirmModal binds its own Escape, and closing both at once would leave
    // the manager unsure which one their keypress acted on.
    if (e.key === 'Escape' && !confirmTarget) closeEditor();
  }

  function closeEditor() {
    editTarget = null;
    form = null;
  }

  function addRow(list) { form[list] = [...form[list], row()]; }
  function removeRow(list, rowUid) {
    const next = form[list].filter((r) => r.uid !== rowUid);
    form[list] = next.length ? next : [row()];
  }

  const filled = (list) => list.filter((r) => r.value.trim());

  const canSave = $derived(
    !!form
    && form.name.trim().length > 0
    && form.description.trim().length >= 10
    && filled(form.examples).length > 0
    && filled(form.replies).length > 0
  );

  async function load() {
    loading = true;
    error = null;
    try {
      items = await api.listSituations({ tenant_id: tenantId });
    } catch (e) {
      error = e.message;
    } finally {
      loading = false;
    }
  }

  $effect(() => { tenantId; load(); });

  async function save() {
    saving = true;
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        example_messages: filled(form.examples).map((r) => r.value.trim()),
        // `id` is carried through deliberately; see api.js.
        variants: filled(form.replies).map((r, i) => ({
          id: r.id, body: r.value.trim(), sort_order: i, is_active: true,
        })),
        priority: Number(form.priority),
        is_active: form.is_active,
      };
      if (editTarget === 'new') {
        await api.createSituation(payload, { tenant_id: tenantId });
        toast.success(`Saved "${payload.name}"`);
      } else {
        await api.updateSituation(editTarget.id, payload, { tenant_id: tenantId });
        toast.success(`Updated "${payload.name}"`);
      }
      closeEditor();
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      saving = false;
    }
  }

  async function remove() {
    const target = confirmTarget;
    confirmTarget = null;
    try {
      await api.deleteSituation(target.id, { tenant_id: tenantId });
      toast.success(`Deleted "${target.name}"`);
      if (editTarget && editTarget !== 'new' && editTarget.id === target.id) closeEditor();
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  }

  async function runTest() {
    if (!testMessage.trim()) return;
    testing = true;
    testResult = null;
    try {
      testResult = await api.testSituationMatch(testMessage.trim(), { tenant_id: tenantId });
    } catch (e) {
      toast.error(e.message);
    } finally {
      testing = false;
    }
  }

  const truncate = (s, n = 70) => (s.length > n ? `${s.slice(0, n)}…` : s);

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'description', label: 'When it applies', render: (r) => truncate(r.description) },
    { key: 'variants', label: 'Replies', align: 'right', render: (r) => String(r.variants.length) },
    { key: 'priority', label: 'Priority', align: 'right', render: (r) => String(r.priority) },
    { key: 'is_active', label: 'Active', render: (r) => (r.is_active ? 'Yes' : 'No') },
    { key: 'match_count_30d', label: 'Matches (30d)', align: 'right',
      render: (r) => String(r.match_count_30d) },
  ];

  /**
   * Starter content, delivered as an editable prefill rather than seeded into
   * the database. Seeding would put this text in every tenant — including the
   * gating suite's, whose tenant id is 1 — and the whole premise of the feature
   * is that the manager owns the wording.
   *
   * Two edits to the observed replies are deliberate. "Cleaners are still at
   * the house" is NOT here: it asserts a fact that is only sometimes true, and
   * a canned reply is given every time. And "just need HER first and last name"
   * became "their" — a canned reply cannot know a visitor's gender.
   */
  const STARTERS = [
    {
      name: 'Early check-in request',
      description:
        'Guest asks to arrive or check in before the standard check-in time, or asks about '
        + 'dropping bags early — including when they name a specific earlier time.',
      priority: 10,
      examples: ['Could we get an earlier check in tomorrow? 2?', 'Any chance of an early check-in?'],
      replies: [
        'Checking with cleaners now',
        "Let me check with the cleaners and I'll get back to you",
        "I'll find out where the cleaners are at and let you know",
      ],
    },
    {
      name: 'Is the place ready? (guest nearby)',
      description:
        'Guest is nearby, on the way, or waiting, and asks whether the house is ready for them.',
      priority: 20,
      examples: ['We are nearby already, is the house ready?', 'Just landed — is it ready?'],
      replies: [
        'Let me check!',
        'Checking now — one sec',
        'Let me find out where things stand and come right back to you',
      ],
    },
    {
      name: 'Extra guest / visitor for the stay',
      description:
        'Guest asks whether they may bring additional people beyond the number on the booking.',
      priority: 30,
      examples: ["My wife's parents may stay one night — do I need to update the stay?"],
      replies: ['No — no problem at all 😊', "That's absolutely fine 😊", 'No problem at all!'],
    },
    {
      name: 'Add someone to the gate / security list',
      description:
        'Guest asks to add a named visitor to the community gate, security, or front-desk list.',
      priority: 40,
      examples: ['Can we add our daughter to the security list?'],
      replies: [
        'No problem. Just need their first and last name.',
        "Happy to — send me their first and last name and I'll get them added.",
        'Sure thing, what’s their full name?',
      ],
    },
  ];

  function startFrom(starter) {
    openNew({
      name: starter.name,
      description: starter.description,
      priority: starter.priority,
      examples: starter.examples.map((m) => row(m)),
      replies: starter.replies.map((b) => row(b)),
    });
  }
</script>

<PageHeader
  title="Saved replies"
  subtitle="When a guest message matches one of these, the draft uses your wording — and always comes to you for review before it's sent.">
  {#snippet children()}
    <button type="button" class="p-btn p-btn-primary" onclick={() => openNew()}>
      <i class="fas fa-plus" aria-hidden="true"></i> New saved reply
    </button>
  {/snippet}
</PageHeader>

{#if error}
  <p class="p-card p-4 text-sm" style="color: var(--p-danger)">{error}</p>
{:else if !loading && !items.length}
  <EmptyState icon="fa-comment-dots" title="No saved replies yet"
              message="Some guest questions aren't about the property at all — they're about how you
                       personally handle something. Write those down once and the draft will use your
                       own words instead of quoting policy back at the guest.">
    {#snippet children()}
      <div class="flex flex-wrap justify-center gap-2">
        {#each STARTERS as starter (starter.name)}
          <button type="button" class="p-btn p-btn-quiet text-xs"
                  onclick={() => startFrom(starter)}>
            Start from “{starter.name}”
          </button>
        {/each}
      </div>
      <p class="mt-3 text-xs p-faint">
        These are examples — they open in the editor for you to change before saving.
      </p>
    {/snippet}
  </EmptyState>
{:else}
  <DataTable {columns} rows={items} {loading}
             emptyMessage="No saved replies yet."
             onRowClick={openEdit} />
  <p class="mt-2 text-xs p-faint">
    Select a saved reply to edit it. Changes take effect within about five minutes.
  </p>

  <!-- The try-it box. See the header comment on why this is not a nicety. -->
  <section class="p-card mt-6 p-4">
    <h2 class="text-sm font-semibold">Try a message</h2>
    <p class="mt-1 text-xs p-muted">
      Paste something a guest might send and see which saved reply it matches. Nothing is sent
      and nothing is saved.
    </p>
    <div class="mt-3 flex flex-wrap gap-2">
      <input class="p-input flex-1" type="text" bind:value={testMessage}
             placeholder="Hi! Could we get an earlier check in tomorrow? 2?"
             onkeydown={(e) => e.key === 'Enter' && runTest()} />
      <button type="button" class="p-btn p-btn-quiet"
              disabled={testing || !testMessage.trim()} onclick={runTest}>
        {testing ? 'Checking…' : 'Try it'}
      </button>
    </div>
    {#if testResult}
      <p class="mt-3 text-sm">
        {#if testResult.matched}
          <i class="fas fa-arrow-right p-faint" aria-hidden="true"></i>
          matches <strong>{testResult.matched.name}</strong>
        {:else if testResult.ran === false}
          <span class="p-muted">{testResult.detail}</span>
        {:else}
          <i class="fas fa-arrow-right p-faint" aria-hidden="true"></i>
          <span class="p-muted">
            no match — this message would be answered from property information as usual
          </span>
        {/if}
      </p>
    {/if}
  </section>
{/if}

<svelte:window on:keydown={form ? onEditorKey : undefined} />

{#if form}
  <!-- Follows ConfirmModal.svelte rather than the older dialog in
       routes/admin/Users.svelte, which trips two a11y warnings: comparing
       target to currentTarget means only backdrop clicks dismiss, so the dialog
       needs no stopPropagation click handler on a non-interactive element, and
       Escape (bound above) is the keyboard equivalent of the backdrop click. -->
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <div class="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4"
       role="presentation"
       onclick={(e) => { if (e.target === e.currentTarget) closeEditor(); }}>
    <div class="p-card max-h-[90vh] w-full max-w-2xl overflow-y-auto p-5" role="dialog"
         aria-modal="true" aria-label="Edit saved reply" tabindex="-1">
      <h2 class="text-sm font-semibold">
        {editTarget === 'new' ? 'New saved reply' : 'Edit saved reply'}
      </h2>

      <div class="mt-4 space-y-4">
        <div>
          <label for="sit-name" class="text-xs font-medium p-muted">Name</label>
          <input id="sit-name" class="p-input mt-1" type="text" bind:value={form.name}
                 placeholder="Early check-in request" />
          <p class="mt-1 text-xs p-faint">Only you see this — it labels the draft when it matches.</p>
        </div>

        <div>
          <label for="sit-desc" class="text-xs font-medium p-muted">When does this apply?</label>
          <textarea id="sit-desc" class="p-input mt-1" rows="3" bind:value={form.description}
                    placeholder="Guest asks to arrive before the standard check-in time…"></textarea>
          <p class="mt-1 text-xs p-faint">
            Describe it the way you would to a new assistant. This is what decides whether a
            message matches, so be specific about what the guest is actually asking for.
          </p>
        </div>

        <div>
          <span class="text-xs font-medium p-muted">Example guest messages</span>
          {#each form.examples as ex (ex.uid)}
            <div class="mt-1 flex gap-2">
              <input class="p-input flex-1" type="text" bind:value={ex.value}
                     placeholder="Could we get an earlier check in tomorrow? 2?" />
              <button type="button" class="p-btn p-btn-quiet px-2"
                      aria-label="Remove example"
                      onclick={() => removeRow('examples', ex.uid)}>
                <i class="fas fa-xmark" aria-hidden="true"></i>
              </button>
            </div>
          {/each}
          <button type="button" class="p-btn p-btn-quiet mt-2 px-2 py-1 text-xs"
                  onclick={() => addRow('examples')}>Add an example</button>
          <p class="mt-1 text-xs p-faint">
            At least one. Real messages are what tell this saved reply apart from the others.
          </p>
        </div>

        <div>
          <span class="text-xs font-medium p-muted">Your replies — we'll rotate between them</span>
          {#each form.replies as reply (reply.uid)}
            <div class="mt-1 flex gap-2">
              <input class="p-input flex-1" type="text" bind:value={reply.value}
                     placeholder="Checking with cleaners now" />
              <button type="button" class="p-btn p-btn-quiet px-2"
                      aria-label="Remove reply"
                      onclick={() => removeRow('replies', reply.uid)}>
                <i class="fas fa-xmark" aria-hidden="true"></i>
              </button>
            </div>
          {/each}
          <button type="button" class="p-btn p-btn-quiet mt-2 px-2 py-1 text-xs"
                  onclick={() => addRow('replies')}>Add another reply</button>
          <p class="mt-1 text-xs p-faint">
            Write replies that are true <em>every</em> time this comes up. If the answer depends on
            the day, write the one that starts the conversation, not the one that states a fact —
            "checking with cleaners now" rather than "the cleaners are still there".
          </p>
        </div>

        <div class="grid gap-4 sm:grid-cols-2">
          <div>
            <label for="sit-priority" class="text-xs font-medium p-muted">Priority</label>
            <input id="sit-priority" class="p-input mt-1" type="number" min="0" max="10000"
                   bind:value={form.priority} />
            <p class="mt-1 text-xs p-faint">Lower wins if two could apply.</p>
          </div>
          <div class="flex items-end">
            <label class="flex items-center gap-2 text-sm">
              <input type="checkbox" bind:checked={form.is_active} />
              <span>Active</span>
            </label>
          </div>
        </div>
      </div>

      {#if editTarget !== 'new' && editTarget.warnings?.length}
        <div class="mt-4 rounded-lg px-3 py-2 text-xs"
             style="background-color: var(--p-warn-bg, rgba(180,120,0,.12))">
          <i class="fas fa-circle-info" aria-hidden="true"></i>
          {#each editTarget.warnings as warning}<p class="mt-1">{warning}</p>{/each}
        </div>
      {/if}

      <div class="mt-5 flex items-center justify-between gap-2">
        {#if editTarget !== 'new'}
          <button type="button" class="p-btn p-btn-quiet px-2 py-1 text-xs"
                  style="color: var(--p-danger)"
                  onclick={() => (confirmTarget = editTarget)}>Delete</button>
        {:else}<span></span>{/if}
        <div class="flex gap-2">
          <button type="button" class="p-btn p-btn-quiet" onclick={closeEditor}>Cancel</button>
          <button type="button" class="p-btn p-btn-primary" disabled={saving || !canSave}
                  onclick={save}>Save</button>
        </div>
      </div>
      {#if !canSave}
        <p class="mt-2 text-right text-xs p-faint">
          Needs a name, a description of at least 10 characters, an example message, and at
          least one reply.
        </p>
      {/if}
    </div>
  </div>
{/if}

<ConfirmModal open={!!confirmTarget}
              title="Delete this saved reply?"
              message={confirmTarget
                ? `"${confirmTarget.name}" will stop matching new messages. Drafts that already used `
                  + `it are kept, but lose the label showing where the wording came from. To retire `
                  + `it without losing that, turn off Active instead.`
                : ''}
              confirmLabel="Delete"
              onConfirm={remove}
              onCancel={() => (confirmTarget = null)} />
